import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import App, {
  preloadToolOrNotFoundRoute,
  shouldPreloadToolOrNotFoundRoute
} from './App';
import { PublicAppProvider } from './contexts/PublicAppContext';
import '@fontsource-variable/manrope';
import '@fontsource-variable/sora';
import '@fontsource-variable/noto-sans-bengali';
import '@fontsource-variable/noto-sans-devanagari';
import './index.css';

const getChunkErrorText = (error) => [
  error?.message,
  error?.name,
  error?.code,
  error?.stack,
  typeof error === 'string' ? error : ''
].filter(Boolean).join(' ');

// Optional homepage showcase chunks (Remotion) fail quietly: the poster stays.
// Recognised by the chunk URL or, for Safari's URL-less import error, by the
// showcase loader claiming that exact error object (see index.html).
const optionalChunks = () => window.__stocktagOptionalChunks
  || (window.__stocktagOptionalChunks = { pending: 0, claimed: new WeakSet() });

const isOptionalChunkError = (error) => (
  /\/assets\/remotion(?:-vendor)?-[\w-]+\.js/i.test(getChunkErrorText(error))
  || optionalChunks().claimed.has(error)
);

// A URL-less import error while the showcase import is in flight may be its
// own; it has to reach the loader's catch before that is known.
const mayBeOptionalChunkError = (error) => optionalChunks().pending > 0
  && !/\/assets\//i.test(getChunkErrorText(error));

const handleChunkLoadError = (error) => {
  const message = getChunkErrorText(error);
  if (!message) return false;
  if (isOptionalChunkError(error)) return false;
  const matches = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading CSS chunk|ERR_TUNNEL_CONNECTION_FAILED|NS_ERROR_CORRUPTED_CONTENT|Expected a JavaScript module script[^]*MIME type|Refused to execute script[^]*MIME type|Minified React error #130\b[^\n]*args\[\]=object|Element type is invalid:[^\n]*got:\s*object/i.test(message);
  if (!matches) return false;
  try {
    if (window.__stocktagRecoveryPending) return true;
    const key = 'stocktag_chunk_reload_once';
    if (sessionStorage.getItem(key)) return false;
    if (window.__stocktagCanAutoReload?.('chunk_load_error') === false) return true;
    sessionStorage.setItem(key, '1');
    window.__stocktagRecoveryPending = true;
    window.location.reload();
    return true;
  } catch {
    return false;
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    handleChunkLoadError(event?.error || event?.message);
  });
  window.addEventListener('unhandledrejection', (event) => {
    handleChunkLoadError(event?.reason);
  });
  window.addEventListener('vite:preloadError', (event) => {
    const error = event?.payload;
    // An optional chunk's error goes on to its own catch instead of reloading.
    if (isOptionalChunkError(error)) return;
    if (mayBeOptionalChunkError(error)) {
      window.setTimeout(() => {
        if (!isOptionalChunkError(error)) handleChunkLoadError(error || 'Failed to fetch dynamically imported module');
      }, 0);
      return;
    }
    event.preventDefault();
    handleChunkLoadError(error || 'Failed to fetch dynamically imported module');
  });
}

const container = document.getElementById('root');
const ssrMode = container?.dataset?.ssrMode || 'replace';
const ssrLanguage = container?.dataset?.ssrLanguage || null;
const ssrTheme = container?.dataset?.ssrTheme || null;
let ssrTexts = null;

if (typeof document !== 'undefined') {
  const localePayload = document.getElementById('__PUBLIC_LOCALE__');
  if (localePayload?.textContent) {
    try {
      ssrTexts = JSON.parse(localePayload.textContent);
    } catch {
      ssrTexts = null;
    }
  }
}

const mountApp = async () => {
  if (!container) return;

  const shouldPreloadPublicToolRoute = typeof window !== 'undefined'
    && shouldPreloadToolOrNotFoundRoute(window.location.pathname, window.location.search);

  if (shouldPreloadPublicToolRoute) {
    try {
      // Keep prerendered landing markup visible while the route bundle loads.
      // In development there is no prerendered markup, but awaiting here still
      // prevents the full-screen application loader from flashing.
      await preloadToolOrNotFoundRoute();
    } catch (error) {
      if (handleChunkLoadError(error)) return;
    }
  }

  const appTree = (
    <React.StrictMode>
      <PublicAppProvider initialLanguage={ssrLanguage} initialTheme={ssrTheme} initialTexts={ssrTexts}>
        <App />
      </PublicAppProvider>
    </React.StrictMode>
  );
  const hasPrerenderedMarkup = Boolean(container.innerHTML.trim());

  if (ssrMode === 'hydrate' && hasPrerenderedMarkup) {
    hydrateRoot(container, appTree);
    return;
  }

  if (ssrMode !== 'static') {
    container.innerHTML = '';
  }

  const root = createRoot(container);
  root.render(appTree);
};

void mountApp();

// Reveal app after React mounts to avoid SSR flash
if (typeof document !== 'undefined') {
  const revealRoot = () => {
    if (document?.body) {
      document.body.classList.add('css-ready');
    }
  };

  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', () => requestAnimationFrame(revealRoot), { once: true });
  } else {
    requestAnimationFrame(revealRoot);
  }
}
